// pdfExport.ts — Panel book PDF generator using jsPDF + jspdf-autotable
// Produces one page per panel with: title block, dimension summary, opening table,
// embed schedule, and materials section.

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { CVProject, PanelData, MpVar } from '../types/panelvar';
import { calcMaterials } from './materialsCalc';

const PAGE_W = 279.4;  // mm (11")
const PAGE_H = 431.8;  // mm (17")
const MARGIN  = 12;

function addHeader(doc: jsPDF, panel: PanelData, project: CVProject) {
  const mp = panel.mpvar;
  // ── Title block ────────────────────────────────────────────────────────────
  doc.setFillColor(14, 32, 60);
  doc.rect(0, 0, PAGE_W, 18, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('ConstructiVision', MARGIN, 11);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Project: ${mp.projectName || project.name}`, PAGE_W / 2, 7, { align: 'center' });
  doc.text(`Job: ${mp.jobNumber || project.jobNumber || '—'}`, PAGE_W / 2, 12, { align: 'center' });

  doc.text(`Mark: ${mp.panelMark}`, PAGE_W - MARGIN, 7, { align: 'right' });
  doc.text(`DWG: ${mp.drawingNumber || '—'}`, PAGE_W - MARGIN, 12, { align: 'right' });
  doc.setTextColor(0, 0, 0);
}

function addDimSection(doc: jsPDF, mp: MpVar, y: number): number {
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Panel Dimensions', MARGIN, y);
  y += 5;

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    theme: 'grid',
    headStyles: { fillColor: [14, 32, 60], textColor: 255 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 40 } },
    head: [['Property', 'Value', 'Property', 'Value']],
    body: [
      ['Width',     `${mp.width}"`,           'Height',      `${mp.height}"`],
      ['Thickness', `${mp.thickness}"`,        'Quantity',     mp.quantity ?? 1],
      ['Top Elev',  `${mp.topElev ?? 0}'`,     'Bot Elev',    `${mp.botElev ?? 0}'`],
      ['Finish',    mp.finish || '—',           'Color',       mp.color || '—'],
      ['Drawn By',  mp.drawBy || '—',           'Checked By',  mp.checkBy || '—'],
      ['Revision',  mp.revision || '—',         'Date',        mp.date || '—'],
    ],
  });

  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
}

function addOpeningSection(doc: jsPDF, panel: PanelData, y: number): number {
  const rows: [string, string, string, string, string][] = [];

  panel.rovar.forEach((s, i) => {
    if (!s.enabled) return;
    const d = s.data;
    rows.push([`RO${i + 1}`, d.mark || '—', `${d.width}" × ${d.height}"`, `${d.x}", ${d.y}"`, 'Rough Opening']);
  });
  panel.wdvar.forEach((s, i) => {
    if (!s.enabled) return;
    const d = s.data;
    rows.push([`W${i + 1}`, d.mark || '—', `${d.width}" × ${d.height}"`, `${d.x}", ${d.y}"`, 'Window']);
  });
  panel.drvar.forEach((s, i) => {
    if (!s.enabled) return;
    const d = s.data;
    rows.push([`D${i + 1}`, d.mark || '—', `${d.width}" × ${d.height}"`, `${d.x}", ${d.y}"`, 'Man Door']);
  });
  panel.dlvar.forEach((s, i) => {
    if (!s.enabled) return;
    const d = s.data;
    rows.push([`DL${i + 1}`, d.mark || '—', `${d.width}" × ${d.height}"`, `${d.x}", ${d.y}"`, 'Dock Leveler']);
  });
  panel.sbvar.forEach((s, i) => {
    if (!s.enabled) return;
    const d = s.data;
    rows.push([`SB${i + 1}`, d.mark || '—', `${d.width}" × ${d.height}"`, `${d.x}", ${d.y}"`, 'Rect Blockout']);
  });
  panel.rbvar.forEach((s, i) => {
    if (!s.enabled) return;
    const d = s.data;
    rows.push([`RB${i + 1}`, d.mark || '—', `Ø${d.diameter}"`, `${d.x}", ${d.y}"`, 'Round Blockout']);
  });

  if (rows.length === 0) return y;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Openings & Blockouts', MARGIN, y);
  y += 5;

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    theme: 'striped',
    headStyles: { fillColor: [14, 32, 60], textColor: 255 },
    head: [['Ref', 'Mark', 'Size', 'Location (X, Y)', 'Type']],
    body: rows,
  });

  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
}

function addEmbedSection(doc: jsPDF, panel: PanelData, y: number): number {
  const rows: [string, string, string, string][] = [];

  panel.bpvar.forEach((s, i) => {
    if (!s.enabled) return;
    rows.push([`BP${i + 1}`, s.data.mark || '—', `${s.data.x}", ${s.data.y}"`, 'Brace Insert']);
  });
  panel.ppvar.forEach((s, i) => {
    if (!s.enabled) return;
    rows.push([`PP${i + 1}`, s.data.mark || '—', `${s.data.x}", ${s.data.y}"`, 'Pick Insert']);
  });
  panel.wcvar.forEach((s, i) => {
    if (!s.enabled) return;
    rows.push([`WC${i + 1}`, s.data.mark || '—', `${s.data.x}", ${s.data.y}"`,
      `Weld Plate ${s.data.width}"×${s.data.height}" t${s.data.thick}"`]);
  });

  if (rows.length === 0) return y;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Embeds & Connections', MARGIN, y);
  y += 5;

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    theme: 'striped',
    headStyles: { fillColor: [14, 32, 60], textColor: 255 },
    head: [['Ref', 'Mark', 'Location (X, Y)', 'Description']],
    body: rows,
  });

  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
}

function addMaterialsSection(doc: jsPDF, panel: PanelData, y: number): number {
  const m = calcMaterials(panel);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Materials & Calculated Properties', MARGIN, y);
  y += 5;

  const fmtPick  = m.pickCount  > 0 ? `${(m.craneLoadPerPickLb / 1000).toFixed(1)} kips` : '—';
  const fmtBrace = m.braceCount > 0 ? `${(m.braceLoadPerPointLb / 1000).toFixed(1)} kips` : '—';

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    theme: 'grid',
    head: [['Item', 'Imperial', 'Metric']],
    headStyles: { fillColor: [14, 32, 60], textColor: 255 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 } },
    body: [
      ['Gross Volume',      `${(m.grossVolCuIn / 1728).toFixed(2)} cu ft`,     ''],
      ['Opening Volume',    `${(m.openingVolCuIn / 1728).toFixed(2)} cu ft`,    ''],
      ['Concrete Volume',   `${m.concreteCuYd.toFixed(3)} yd³`,                `${m.concreteCuM.toFixed(3)} m³`],
      ['Estimated Weight',  `${m.panelWeightKips.toFixed(1)} kips (${m.panelWeightTons.toFixed(2)} tons)`,
                            `${Math.round(m.panelWeightKg).toLocaleString()} kg`],
      ['Ext. Face Area',    `${m.exteriorFaceAreaSqFt.toFixed(1)} sq ft`,       `${m.formAreaSqM.toFixed(1)} m²`],
      ['Finished Area',     `${m.finishedAreaSqFt.toFixed(1)} sq ft`,           `${m.finishedAreaSqM.toFixed(1)} m²`],
      ['Form Area',         `${m.formAreaSqFt.toFixed(1)} sq ft`,               `${m.formAreaSqM.toFixed(1)} m²`],
      ['CG (from 0,0)',     `X = ${m.cgX.toFixed(2)}″  Y = ${m.cgY.toFixed(2)}″`,
                            `X = ${Math.round(m.cgX * 25.4)} mm  Y = ${Math.round(m.cgY * 25.4)} mm`],
      ['Slenderness (H/T)', m.slendernessRatio.toFixed(1),                      ''],
      ['Load per Pick',     fmtPick,                                            ''],
      ['Load per Brace',    fmtBrace,                                           ''],
    ],
  });

  return (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
}

/** Build the panel book as bytes. Returns null if no valid panels exist. */
export function buildPanelBookBytes(project: CVProject): Uint8Array | null {
  if (!project.panels.length) return null;

  const doc = new jsPDF({
    orientation: 'portrait',
    unit:        'mm',
    format:      [PAGE_W, PAGE_H],
  });

  let first = true;
  for (const panel of project.panels) {
    const mp = panel.mpvar;
    if (!mp.width || !mp.height || mp.width <= 0 || mp.height <= 0) {
      if (import.meta.env.DEV) console.warn(`[CV] buildPanelBookBytes: skipping panel "${mp.panelMark}" — invalid dimensions`);
      continue;
    }
    if (!first) doc.addPage([PAGE_W, PAGE_H]);
    first = false;

    try {
      addHeader(doc, panel, project);
      let y = 24;
      y = addDimSection(doc, panel.mpvar, y);
      y = addOpeningSection(doc, panel, y);
      y = addEmbedSection(doc, panel, y);
      addMaterialsSection(doc, panel, y);
    } catch (err) {
      if (import.meta.env.DEV) console.error(`[CV] buildPanelBookBytes: error on panel "${mp.panelMark}":`, err);
      // Continue to next panel — one bad panel shouldn't abort the whole book
    }
  }

  if (first) return null; // all panels skipped

  return new Uint8Array(doc.output('arraybuffer'));
}

export function panelBookFilename(project: CVProject): string {
  return (project.name || 'panel-book').replace(/\s+/g, '_') + '.pdf';
}
